<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from 'vue-toastification/dist/index.mjs'
import { PerfectScrollbar } from 'vue3-perfect-scrollbar'
import axios from 'axios'

const router = useRouter()
const toast = useToast()

const notifications = ref([])
const conflicts = ref([])
const seenIds = ref(new Set(JSON.parse(localStorage.getItem('autoMatchBell.seen') || '[]')))
const seenConflictIds = ref(new Set(JSON.parse(localStorage.getItem('autoMatchBell.seenConflicts') || '[]')))
const loading = ref(false)
// Tab: 'matches' (auto-matched claims) | 'conflicts' (rejected claims)
const tab = ref('matches')
let channel = null
let pollTimer = null

const unseenMatchesCount = computed(() =>
  notifications.value.filter(n => !seenIds.value.has(n.id)).length
)
const unseenConflictsCount = computed(() =>
  conflicts.value.filter(c => !seenConflictIds.value.has(c.id)).length
)
const unseenCount = computed(() => unseenMatchesCount.value + unseenConflictsCount.value)

const persistSeen = () => {
  localStorage.setItem('autoMatchBell.seen', JSON.stringify([...seenIds.value].slice(-200)))
}
const persistSeenConflicts = () => {
  localStorage.setItem('autoMatchBell.seenConflicts', JSON.stringify([...seenConflictIds.value].slice(-200)))
}

const fetchRecent = async () => {
  loading.value = true
  try {
    // Fetch both buckets in parallel — same endpoint family. If the
    // conflicts endpoint isn't deployed yet, treat as empty so the bell
    // still works for the matches stream.
    const [matchesRes, conflictsRes] = await Promise.all([
      axios.get('/shipment/auto-match-notifications', { params: { hours: 24, limit: 50 } }),
      axios.get('/shipment/auto-match-notifications/conflicts', { params: { hours: 24 * 7, limit: 50 } }).catch(() => ({ data: { success: true, data: [] } })),
    ])
    if (matchesRes.data?.success) notifications.value = matchesRes.data.data || []
    if (conflictsRes.data?.success) conflicts.value = conflictsRes.data.data || []
  } catch (e) {
    // bell is non-critical, but log so we don't debug blind
    console.warn('[AutoMatchBell] fetch failed:', e?.response?.status, e?.message)
  } finally {
    loading.value = false
  }
}

const userTeamCode = () => {
  try {
    const raw = JSON.parse(localStorage.getItem('fullUserData') || '{}')
    return raw?.team?.code || raw?.Team?.code || null
  } catch {
    return null
  }
}

const subscribeRealtime = () => {
  if (!window.Echo) {
    setTimeout(subscribeRealtime, 2000)
    return
  }
  const code = userTeamCode()
  const channelName = code ? `team.${code}.parcels` : 'parcels.general'
  try {
    channel = window.Echo.channel(channelName)
    const handler = payload => {
      const row = {
        id: payload?.claim?.id,
        matched_at: payload?.claim?.matched_at,
        channel: payload?.claim?.channel,
        photo_url: payload?.claim?.photo_url,
        barcode: payload?.parcel?.barcode,
        parcel_id: payload?.parcel?.id,
        team: payload?.team,
        contact: payload?.contact,
      }
      if (!row.id) return
      // Dedup by id
      if (!notifications.value.find(n => n.id === row.id)) {
        notifications.value.unshift(row)
        if (notifications.value.length > 50) notifications.value.length = 50
        toast.info(
          `📸 ${row.contact?.name || 'Client'} a réclamé ${row.barcode} via ${row.channel || 'chatbot'}`,
          { timeout: 7000 }
        )
      }
    }
    channel.listen('ParcelAutoMatchedFromClaim', handler)
    channel.listen('.ParcelAutoMatchedFromClaim', handler)
    channel.listen('Modules\\Shipment\\Events\\ParcelAutoMatchedFromClaim', handler)
  } catch (e) {
    // retry in 5s
    setTimeout(subscribeRealtime, 5000)
  }
}

const markAllSeen = () => {
  notifications.value.forEach(n => seenIds.value.add(n.id))
  conflicts.value.forEach(c => seenConflictIds.value.add(c.id))
  persistSeen()
  persistSeenConflicts()
}

const formatRelative = iso => {
  if (!iso) return ''
  const diff = (Date.now() - new Date(iso).getTime()) / 1000
  if (diff < 60) return `${Math.floor(diff)}s`
  if (diff < 3600) return `${Math.floor(diff / 60)}min`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`
  return `${Math.floor(diff / 86400)}j`
}

const openReassign = n => {
  router.push({ path: '/shipment/claim-parcel', query: { barcode: n.barcode } })
}

// Conflict-specific: open the claim page with both the barcode AND the
// claimer's contact id so the operator can confirm "yes, reassign to
// this person" in one click instead of searching for the customer.
const openReassignToClaimer = c => {
  router.push({
    path: '/shipment/claim-parcel',
    query: {
      barcode: c.barcode,
      target_contact_id: c.claimer?.id,
    },
  })
}

// Operator decided the current owner is correct — hide this conflict
// (status flips to 'expired' on backend, audit row stays).
const dismissConflict = async c => {
  try {
    const { data } = await axios.post(`/shipment/auto-match-notifications/conflicts/${c.id}/dismiss`)
    if (data?.success) {
      // Optimistically remove from local list so the badge updates.
      conflicts.value = conflicts.value.filter(x => x.id !== c.id)
      toast.success(`Conflit pour ${c.barcode} ignoré.`, { timeout: 3000 })
    }
  } catch (e) {
    toast.error('Impossible d\'ignorer ce conflit', { timeout: 5000 })
    console.warn('[AutoMatchBell] dismiss failed:', e?.response?.status, e?.message)
  }
}

const openParcel = n => {
  if (n.parcel_id) router.push({ path: `/shipment/incoming-parcels/${n.parcel_id}` })
}

const openPhoto = url => {
  if (url) window.open(url, '_blank')
}

onMounted(() => {
  fetchRecent()
  subscribeRealtime()
  // light poll every 2 min as a safety net if Echo drops
  pollTimer = setInterval(fetchRecent, 120000)
})

onBeforeUnmount(() => {
  if (pollTimer) clearInterval(pollTimer)
  if (window.Echo && channel) {
    const code = userTeamCode()
    const channelName = code ? `team.${code}.parcels` : 'parcels.general'
    try { window.Echo.leaveChannel(channelName) } catch {}
    channel = null
  }
})
</script>

<template>
  <IconBtn id="auto-match-bell-btn">
    <VBadge
      :model-value="unseenCount > 0"
      :content="unseenCount > 99 ? '99+' : unseenCount"
      color="warning"
      offset-x="2"
      offset-y="3"
    >
      <VIcon icon="tabler-camera" />
    </VBadge>

    <VMenu
      activator="parent"
      width="420px"
      offset="12px"
      :close-on-content-click="false"
      @update:model-value="v => v && markAllSeen()"
    >
      <VCard>
        <VCardItem class="pa-3">
          <VCardTitle class="text-h6 d-flex align-center gap-2">
            <VIcon icon="tabler-camera" size="20" color="warning" />
            Réclamations chatbot
          </VCardTitle>
        </VCardItem>

        <VTabs v-model="tab" density="compact" grow>
          <VTab value="matches">
            Matches (24h)
            <VChip v-if="notifications.length" size="x-small" color="warning" class="ms-2">
              {{ notifications.length }}
            </VChip>
          </VTab>
          <VTab value="conflicts">
            Conflits (7j)
            <VChip v-if="conflicts.length" size="x-small" color="error" class="ms-2">
              {{ conflicts.length }}
            </VChip>
          </VTab>
        </VTabs>

        <VDivider />

        <VWindow v-model="tab">
          <!-- TAB 1: matched claims (existing) -->
          <VWindowItem value="matches">
            <PerfectScrollbar :options="{ wheelPropagation: false }" style="max-height: 28rem">
              <VList v-if="notifications.length" class="py-0">
                <template v-for="(n, i) in notifications" :key="n.id">
                  <VDivider v-if="i > 0" />
                  <VListItem class="py-3">
                    <div class="d-flex align-start gap-3">
                      <VAvatar v-if="n.photo_url" size="56" rounded class="cursor-pointer" @click="openPhoto(n.photo_url)">
                        <VImg :src="n.photo_url" cover />
                      </VAvatar>
                      <VAvatar v-else color="warning" variant="tonal" size="56" rounded>
                        <VIcon icon="tabler-camera" size="24" />
                      </VAvatar>

                      <div class="flex-grow-1 min-w-0">
                        <div class="d-flex align-center gap-2 mb-1">
                          <span class="text-sm font-weight-medium text-truncate">
                            {{ n.contact?.name || 'Client inconnu' }}
                          </span>
                          <VChip v-if="n.contact?.client_id" size="x-small" variant="tonal" color="primary">
                            {{ n.contact.client_id }}
                          </VChip>
                        </div>
                        <p class="text-body-2 mb-1" style="font-family: monospace">
                          {{ n.barcode }}
                        </p>
                        <div class="d-flex align-center gap-2 text-disabled text-xs">
                          <VIcon size="14" icon="tabler-clock" />
                          <span>{{ formatRelative(n.matched_at) }}</span>
                          <span>·</span>
                          <span>{{ n.channel || 'chatbot' }}</span>
                          <VChip v-if="n.team?.code" size="x-small" variant="outlined">
                            {{ n.team.code }}
                          </VChip>
                        </div>
                      </div>
                    </div>
                    <div class="d-flex gap-2 mt-2">
                      <VBtn size="x-small" variant="tonal" color="primary" @click="openParcel(n)">
                        Voir colis
                      </VBtn>
                      <VBtn size="x-small" variant="text" color="warning" @click="openReassign(n)">
                        Réassigner
                      </VBtn>
                    </div>
                  </VListItem>
                </template>
              </VList>

              <VListItem v-else class="text-center text-medium-emphasis py-6">
                <VListItemTitle>Aucune réclamation auto-assignée récente.</VListItemTitle>
              </VListItem>
            </PerfectScrollbar>
          </VWindowItem>

          <!-- TAB 2: conflicts — parcel already attached to a different
               customer when the bot saw a new claim. Operator decides
               between keeping current owner or reassigning to claimer. -->
          <VWindowItem value="conflicts">
            <PerfectScrollbar :options="{ wheelPropagation: false }" style="max-height: 28rem">
              <VList v-if="conflicts.length" class="py-0">
                <template v-for="(c, i) in conflicts" :key="c.id">
                  <VDivider v-if="i > 0" />
                  <VListItem class="py-3">
                    <div class="d-flex align-start gap-3">
                      <VAvatar v-if="c.photo_url" size="56" rounded class="cursor-pointer" @click="openPhoto(c.photo_url)">
                        <VImg :src="c.photo_url" cover />
                      </VAvatar>
                      <VAvatar v-else color="error" variant="tonal" size="56" rounded>
                        <VIcon icon="tabler-alert-triangle" size="24" />
                      </VAvatar>

                      <div class="flex-grow-1 min-w-0">
                        <p class="text-body-2 mb-1" style="font-family: monospace">
                          {{ c.barcode }}
                        </p>
                        <!-- Two-sided display: who claims it, who has it -->
                        <div class="text-xs mb-1">
                          <VIcon size="12" icon="tabler-user-question" class="me-1" />
                          <span class="font-weight-medium">{{ c.claimer?.name || 'Réclamant' }}</span>
                          <VChip v-if="c.claimer?.client_id" size="x-small" variant="tonal" color="warning" class="ms-1">
                            {{ c.claimer.client_id }}
                          </VChip>
                          <span class="text-disabled"> dit que c’est à lui</span>
                        </div>
                        <div class="text-xs">
                          <VIcon size="12" icon="tabler-user-check" class="me-1" />
                          <span class="text-disabled">Actuellement attribué à </span>
                          <span class="font-weight-medium">{{ c.current_owner?.name || '—' }}</span>
                          <VChip v-if="c.current_owner?.client_id" size="x-small" variant="tonal" color="primary" class="ms-1">
                            {{ c.current_owner.client_id }}
                          </VChip>
                        </div>
                        <div class="d-flex align-center gap-2 text-disabled text-xs mt-1">
                          <VIcon size="14" icon="tabler-clock" />
                          <span>{{ formatRelative(c.reported_at) }}</span>
                          <span>·</span>
                          <span>{{ c.channel || 'chatbot' }}</span>
                          <VChip v-if="c.team?.code" size="x-small" variant="outlined">
                            {{ c.team.code }}
                          </VChip>
                        </div>
                      </div>
                    </div>
                    <div class="d-flex gap-2 mt-2 flex-wrap">
                      <VBtn size="x-small" variant="tonal" color="primary" @click="openParcel(c)">
                        Voir colis
                      </VBtn>
                      <VBtn size="x-small" variant="tonal" color="warning" @click="openReassignToClaimer(c)">
                        Réassigner au réclamant
                      </VBtn>
                      <VBtn size="x-small" variant="text" color="secondary" @click="dismissConflict(c)">
                        Ignorer
                      </VBtn>
                    </div>
                  </VListItem>
                </template>
              </VList>

              <VListItem v-else class="text-center text-medium-emphasis py-6">
                <VListItemTitle>Aucun conflit à examiner.</VListItemTitle>
              </VListItem>
            </PerfectScrollbar>
          </VWindowItem>
        </VWindow>
      </VCard>
    </VMenu>
  </IconBtn>
</template>
